import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Search, Filter, FileText, Eye, CheckCircle, AlertTriangle, XCircle, Copy, X, Loader2 } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import { StatusBadge } from '../../../components/offerte/StatusBadge'
import { PermissionGate } from '../../../components/PermissionGate'
import { OffertaList, StatoOfferta, Cliente } from '../../../types/offerte'

export default function OffertePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [offerte, setOfferte] = useState<OffertaList[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState(searchParams.get('q') || '')
  const [filterStato, setFilterStato] = useState<StatoOfferta | ''>((searchParams.get('stato') as StatoOfferta) || '')
  const [clienti, setClienti] = useState<Cliente[]>([])
  const [filterCliente, setFilterCliente] = useState('')

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [duplicaOfferta, setDuplicaOfferta] = useState<OffertaList | null>(null)
  const [duplicaClienteId, setDuplicaClienteId] = useState('')
  const [duplicaTitolo, setDuplicaTitolo] = useState('')
  const [duplicaLoading, setDuplicaLoading] = useState(false)
  const [duplicaSearch, setDuplicaSearch] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  // Aggiorna search se MAIA naviga con query param
  useEffect(() => {
    const q = searchParams.get('q')
    if (q && q !== search) setSearch(q)
    const stato = searchParams.get('stato') as StatoOfferta | null
    if (stato && stato !== filterStato) setFilterStato(stato)
  }, [searchParams])

  const loadData = async () => {
    try {
      setLoading(true)
      const [offerteRes, clientiRes] = await Promise.all([
        api.get('/v1/offerte'),
        api.get('/v1/anagrafiche/clienti')
      ])
      setOfferte(offerteRes.data)
      setClienti(clientiRes.data)
    } catch (err) {
      console.error('Errore caricamento:', err)
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number | string) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value) || 0)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('it-IT')
  }

  const handleDuplica = async () => {
    if (!duplicaOfferta || !duplicaClienteId) return
    try {
      setDuplicaLoading(true)
      const res = await api.post(`/v1/offerte/${duplicaOfferta.id}/duplica`, {
        cliente_id: duplicaClienteId,
        titolo: duplicaTitolo || undefined,
      })
      setDuplicaOfferta(null)
      setDuplicaClienteId('')
      setDuplicaTitolo('')
      setDuplicaSearch('')
      setToast({ message: `Offerta duplicata: ${res.data.numero_offerta}`, type: 'success' })
      navigate(`/app/offerte/${res.data.id}`)
    } catch (err: any) {
      setToast({ message: err?.response?.data?.detail || 'Errore nella duplicazione', type: 'error' })
    } finally {
      setDuplicaLoading(false)
    }
  }

  const filteredClientiDuplica = clienti.filter(c =>
    c.attivo && c.ragione_sociale.toLowerCase().includes(duplicaSearch.toLowerCase())
  )

  const filteredOfferte = offerte.filter(off => {
    const matchSearch = off.titolo.toLowerCase().includes(search.toLowerCase()) ||
      off.numero_offerta.toLowerCase().includes(search.toLowerCase()) ||
      off.cliente.ragione_sociale.toLowerCase().includes(search.toLowerCase())
    const matchStato = !filterStato || off.stato === filterStato
    const matchCliente = !filterCliente || off.cliente.id === filterCliente
    return matchSearch && matchStato && matchCliente
  })

  return (
    <div>
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Offerte</h1>
          <p className="text-gray-500">Gestione preventivi</p>
        </div>
        <PermissionGate module="offerte" action="create">
          <button
            onClick={() => navigate('/app/offerte/nuova')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuova Offerta
          </button>
        </PermissionGate>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca offerte..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Filtro Stato */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value as StatoOfferta | '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white"
            >
              <option value="">Tutti gli stati</option>
              <option value="bozza">Bozza</option>
              <option value="in_corso">In Corso</option>
              <option value="in_revisione">In Revisione</option>
              <option value="approvata">Approvata</option>
              <option value="in_attesa">In Attesa</option>
              <option value="confermata">Confermata</option>
              <option value="rifiutata">Rifiutata</option>
            </select>
          </div>

          {/* Filtro Cliente */}
          <div>
            <select
              value={filterCliente}
              onChange={(e) => setFilterCliente(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white"
            >
              <option value="">Tutti i clienti</option>
              {clienti.filter(c => c.attivo).map(cliente => (
                <option key={cliente.id} value={cliente.id}>{cliente.ragione_sociale}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filteredOfferte.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            {search || filterStato || filterCliente
              ? 'Nessuna offerta trovata con i filtri selezionati'
              : 'Nessuna offerta presente. Crea la tua prima offerta!'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numero</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valore</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margine</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scadenza</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOfferte.map((offerta) => (
                <tr
                  key={offerta.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/app/offerte/${offerta.id}`)}
                >
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {offerta.numero_offerta}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{offerta.titolo}</div>
                    {offerta.citta_consegna && (
                      <div className="text-sm text-gray-500">{offerta.citta_consegna}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{offerta.cliente.ragione_sociale}</td>
                  <td className="px-6 py-4">
                    <StatusBadge stato={offerta.stato} size="sm" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    {offerta.stock_status === 'ok' && (
                      <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                    )}
                    {offerta.stock_status === 'parziale' && (
                      <AlertTriangle className="w-4 h-4 text-amber-500 mx-auto" />
                    )}
                    {offerta.stock_status === 'insufficiente' && (
                      <XCircle className="w-4 h-4 text-red-500 mx-auto" />
                    )}
                    {!offerta.stock_status && (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">
                    {formatCurrency(offerta.totale_valore)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={`font-medium ${
                      Number(offerta.margine_percentuale) >= 20
                        ? 'text-green-600'
                        : Number(offerta.margine_percentuale) >= 10
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}>
                      {Number(offerta.margine_percentuale).toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {formatDate(offerta.data_scadenza)}
                  </td>
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => navigate(`/app/offerte/${offerta.id}`)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Visualizza"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDuplicaOfferta(offerta)
                          setDuplicaTitolo(offerta.titolo)
                          setDuplicaClienteId('')
                          setDuplicaSearch('')
                        }}
                        className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                        title="Duplica su altro cliente"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Duplica Offerta */}
      {duplicaOfferta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Duplica Offerta</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {duplicaOfferta.numero_offerta} — {duplicaOfferta.cliente.ragione_sociale}
                </p>
              </div>
              <button onClick={() => setDuplicaOfferta(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titolo nuova offerta</label>
                <input
                  type="text"
                  value={duplicaTitolo}
                  onChange={(e) => setDuplicaTitolo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente destinazione *</label>
                <input
                  type="text"
                  placeholder="Cerca cliente..."
                  value={duplicaSearch}
                  onChange={(e) => setDuplicaSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                />
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredClientiDuplica.length === 0 ? (
                    <p className="p-3 text-sm text-gray-400 text-center">Nessun cliente trovato</p>
                  ) : (
                    filteredClientiDuplica.map(c => (
                      <button
                        key={c.id}
                        onClick={() => setDuplicaClienteId(c.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0 ${
                          duplicaClienteId === c.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {c.ragione_sociale}
                        {c.citta && <span className="text-gray-400 ml-2">({c.citta})</span>}
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                Saranno copiati: costi, voci, scaglioni di pagamento, canoni ricorsivi.
                {duplicaOfferta.stock_status && ' Il documento Word verra\' aggiornato con i dati del nuovo cliente.'}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setDuplicaOfferta(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleDuplica}
                disabled={!duplicaClienteId || duplicaLoading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {duplicaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                Duplica
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
